import type { IMappedGeoMappingItem } from 'partner360/components/filters'

import { normalizeGeoKey } from './normalizeGeoKey'

/** One country's `geoMappingItems` ancestor chain, root first — arbitrary length, since
 *  `geoMappingItems` is a generic `parentId` tree with no fixed number of levels and different
 *  branches can end at different depths. */
export type TGeoChain = IMappedGeoMappingItem[]

/** The two lookups the Geo axis needs to reproduce the `GeoMapping` filter's tree. */
export interface IGeoMappingTree {
  /** Normalized country code → the ancestor chain of the *deepest* node covering it. */
  chainByCountry: Map<string, TGeoChain>
  /** `itemId` → the node's position in `geoMappingItems`, i.e. the tree's own display order.
   *  `TreeSelect` nests the flat array in array order (`getTreeStructure` pushes each child onto
   *  its parent as it iterates), and the array arrives `order`-sorted from
   *  `fetchFinancialCockpitData`, so a node's position is exactly where the filter shows it among
   *  its siblings. `order` itself is a single tree-wide integer rather than a per-level one, so it
   *  can't break sibling ties on its own. */
  positionById: Map<string, number>
}

/** `item`'s chain up to its root, root first. Mirrors `getTreeStructure`'s root rule: a `parentId`
 *  that doesn't resolve in the set (or is empty) ends the chain there rather than discarding the
 *  branch, the same way that function promotes an orphan to a root. The `visited` set keeps a
 *  malformed `parentId` cycle from looping forever. */
const getAncestorChain = (
  item: IMappedGeoMappingItem,
  itemsById: Map<string, IMappedGeoMappingItem>
): TGeoChain => {
  const chain = [item]
  const visited = new Set([item.itemId])

  let current = item
  while (current.parentId) {
    const parent = itemsById.get(current.parentId)
    if (!parent || visited.has(parent.itemId)) {
      break
    }
    chain.unshift(parent)
    visited.add(parent.itemId)
    current = parent
  }

  return chain
}

/** Every country code the `geoMappingItems` tree covers, mapped to its own root→leaf chain — the
 *  Geo axis's whole source of structure. Joined against `IEnrichedCockpitFinSobRow.countryCode`
 *  (the same `CountryCode` `getFilteredCockpitPartnerData`'s `matchesGeoMappings` matches on,
 *  resolved onto the row once by `selectEnrichedCockpitFinSobRows`). Computed once per tree rather
 *  than per row. A country the tree doesn't cover simply has no entry — callers treat that as
 *  Unmapped.
 *
 *  A node's `countries` is the roll-up of its *whole subtree*, not just its own level — `North
 *  America` carries `['CA','US']` and its `USA` child carries `['US']` (which is why
 *  `mapSobAnalyticsReportJoinedToQuarterlyHierarchyNodes` can derive `hasChildren: countries.length
 *  > 1`). So a country code matches every node along its branch, and only the **deepest** match
 *  identifies where it actually sits; taking the first match instead collapses the axis to its root
 *  level. `H2PlanningReviewWidget` resolves a code the same way, preferring the
 *  `countries.length === 1` node before falling back to any node containing it — "deepest wins" is
 *  that heuristic generalized to a tree of any shape, and it degrades gracefully where a strict
 *  leaf-only rule wouldn't: a country listed on a node whose children don't list it (`AT` on `DA`
 *  with no `Austria` node) still resolves to that node instead of falling to Unmapped.
 *
 *  Ties — two nodes at equal depth covering one country — keep the earlier array item, so the
 *  result is deterministic and follows the order the filter itself renders in. */
export const buildGeoMappingTree = (geoMappingItems: IMappedGeoMappingItem[]): IGeoMappingTree => {
  const itemsById = new Map(geoMappingItems.map((item) => [item.itemId, item]))
  const chainByCountry = new Map<string, TGeoChain>()
  const positionById = new Map<string, number>()

  geoMappingItems.forEach((item, position) => {
    /** Last one wins on a duplicated `itemId`, matching both `itemsById` above and the filter's own
     *  `getItemsMap`, so a malformed payload still resolves an id to one item *and* one position
     *  rather than mixing one item's name with another's place among its siblings. */
    positionById.set(item.itemId, position)

    /** Once per item rather than once per country — every country it covers shares this chain. */
    const chain = getAncestorChain(item, itemsById)

    item.countries.forEach((country) => {
      const key = normalizeGeoKey(country)

      if (!key) {
        return
      }

      const current = chainByCountry.get(key)

      if (!current || chain.length > current.length) {
        chainByCountry.set(key, chain)
      }
    })
  })

  return { chainByCountry, positionById }
}
